import { NameColumn } from '@/components/NameColumn';
import type { Columns } from '@/components/table/types';
import { VulnerabilityStatusComponent } from '@/sections/vulnerabilities/components/VulnerabilityStatus';
import type { Risk } from '@/types';

/**
 * Builds column definitions for Risk entity tables
 *
 * Returns columns for:
 * - status: Risk status (Pending/Open/Closed/Accepted/Rejected) - FIRST
 * - name: Vulnerability name (bold purple) with DNS below - SECOND
 *
 * Column order follows platform convention (status-first) to align with
 * main tables (Assets, Seeds, Vulnerabilities) for consistent UX.
 * In global mode, Customer Name is prepended, resulting in:
 * [Customer Name, Status, Vulnerability]
 *
 * The name column uses NameColumn component to display:
 * - Risk name in bold purple text
 * - DNS field below in smaller gray text
 *
 * Status is extracted from the 'class' field's first character:
 * - 'T' → Pending
 * - 'O' → Open
 * - 'R' → Closed
 * - 'I' → Accepted
 * - 'D' → Rejected
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildRiskColumns();
 * // Returns array with status and name columns (status first)
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildRiskColumns(): Columns<Risk> {
  return [
    {
      id: 'status',
      label: 'Status',
      cell: (item: Risk) =>
        item?.status ? (
          <VulnerabilityStatusComponent vulnerability={item} />
        ) : (
          '-'
        ),
    },
    {
      id: 'name',
      label: 'Vulnerability',
      cell: (item: Risk) => {
        if (!item?.name) {
          return '-';
        }
        return <NameColumn name={item.name} description={item.dns} />;
      },
    },
  ];
}
